import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { ANALYSIS_MARGIN, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER, useSplitView } from '../../../../hooks/useSplitView';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { fetchAllDropdownListsStarted } from '../../../admin/dropdown-lists/store';
import { fetchAllNettingEntitiesStarted } from '../../../admin/entity/store';
import { DocumentViewerType } from '../../../documents/document-viewer/store';
import { AnalysisView } from '../../../documents/store/types';
import { AnalysisViewButtons } from '../../../shared/analysis/AnalysisViewButtons';
import { DocumentAnalysisViewer } from '../../../shared/analysis/DocumentAnalysisViewer';
import { Button } from '../../../shared/button/Button';
import { calculateNettingEngineStarted, getCurrentNettingEngineDocumentIsValid, getDocumentSummaryView, toggleDocumentSummaryView } from '../store';
import styles from './NettingEngineDocuments.module.scss';
import { NettingEngineDocumentSummary } from './NettingEngineDocumentSummary';

const HEADER_AND_BUTTONS_WITH_MARGIN = 120;

export const NettingEngineDocumentAnalysis: React.FC = () => {
    const [autoUpdateOnCalculate, setAutoUpdateOnCalculate] = useState<boolean>(false);
    const history = useHistory();
    const dispatch = useAppDispatch();
    useFetchStarted([fetchAllDropdownListsStarted(), fetchAllNettingEntitiesStarted()]);
    const currentNettingEngineDocumentIsValid = useAppSelector(getCurrentNettingEngineDocumentIsValid);
    const documentSummaryView = useAppSelector(getDocumentSummaryView);

    const [screenWidth, screenHeight] = useWindowResize();
    const [pageWidth, pageHeight] = useSplitView(documentSummaryView, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER);

    const toggleAnalysisView = useCallback((view: AnalysisView) => view !== documentSummaryView && dispatch(toggleDocumentSummaryView(view)), [dispatch, documentSummaryView]);

    const back = useCallback(() => {
        history.push('/opinions/netting-engine/documents');
        dispatch(toggleDocumentSummaryView(AnalysisView.DATASET));
    }, [history, dispatch]);

    const calculate = useCallback(() => dispatch(calculateNettingEngineStarted(autoUpdateOnCalculate)), [dispatch, autoUpdateOnCalculate]);

    const analysisWrapperDimensions = { height: `${screenHeight - NAV_BAR - ANALYSIS_MARGIN - HEADER_AND_BUTTONS_WITH_MARGIN}px`, width: `${screenWidth}px` };

    const pageDimensions = { height: `${pageHeight - HEADER_AND_BUTTONS_WITH_MARGIN}px`, width: `${pageWidth}px` };

    return (
        <div className={styles.summaryWrapper}>
            <div className={styles.summaryTitle}>Summary</div>
            <div className={styles.analysisWrapper} style={analysisWrapperDimensions}>
                <div className={styles.sideMenuWrapper}>
                    <AnalysisViewButtons toggleAnalysisView={toggleAnalysisView} currentView={documentSummaryView} disabled={false} />
                </div>
                <div className={styles.splitWrapper}>
                    {documentSummaryView !== AnalysisView.DATASET &&
                        <div className={styles.documentAnalysisWrapper} style={pageDimensions}>
                            <DocumentAnalysisViewer width={pageWidth} type={DocumentViewerType.NETTING_ANALYSIS} />
                        </div>
                    }
                    {documentSummaryView !== AnalysisView.DOCUMENT &&
                        <div className={styles.datasetAnalysisWrapper} style={pageDimensions}>
                            <NettingEngineDocumentSummary autoUpdateOnCalculate={autoUpdateOnCalculate} setAutoUpdateOnCalculate={setAutoUpdateOnCalculate} />
                        </div>
                    }
                </div>
            </div>
            <div className={styles.surveyButtonCalculate}>
                <Button label='Back' onClick={back} />
                <Button label='Calculate' disabled={!currentNettingEngineDocumentIsValid} onClick={calculate} />
            </div>
        </div>
    );
};
